import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import { motion } from "framer-motion";
import { getUserDocId } from "../../utils/userDocId";
import logoImg from "../../assets/logo.png";

export default function RegistrationAndDocuments() {
  const { user } = useAuth();

  const [summary, setSummary] = useState({
    mandatory: 0,
    training: 0,
    forms: 0,
    references: 0,
    overall: 0,
  });

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [referenceProgress, setReferenceProgress] = useState(0);

  // NEW: active tab for three-tabbed menu
  const [activeTab, setActiveTab] = useState("documents"); // "documents" | "forms" | "references"

  useEffect(() => {
    if (!user?.email) return;
    const userId = getUserDocId(user);

    console.log("📡 Listening for updates from:", userId);

    // Listen for admin summary updates
    const summaryUnsub = onSnapshot(doc(db, "users", userId), (snap) => {
      if (snap.exists() && snap.data().complianceSummary) {
        const data = snap.data().complianceSummary;
        setSummary(data);
        console.log("✅ Synced summary from admin:", data);
        setSynced(true);
      } else {
        console.log("⚠️ No compliance summary found yet for this user");
      }
    });

    // Listen for compliance documents
    const docsUnsub = onSnapshot(
      collection(db, "users", userId, "complianceDocs"),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDocs(list);
        setLoading(false);
        console.log("📄 Synced compliance docs:", list);
      },
      (error) => {
        console.error("❌ Error listening for complianceDocs:", error);
        setLoading(false);
      }
    );

    return () => {
      summaryUnsub();
      docsUnsub();
    };
  }, [user]);

  // simple progress bar component
  const ProgressBar = ({ value, color = "#1e3a8a" }) => (
    <div
      style={{
        backgroundColor: "#e5e7eb",
        borderRadius: 10,
        height: 8,
        width: "100%",
      }}
    >
      <div
        style={{
          width: `${value}%`,
          height: "100%",
          backgroundColor: color,
          borderRadius: 10,
          transition: "width .3s ease-in-out",
        }}
      />
    </div>
  );

  if (!user?.email) {
    return <div style={{ padding: 20 }}>Please log in to view compliance.</div>;
  }

  // ---------- CATEGORY LOGIC FOR TABS ----------

  const normalizeId = (id) => (id || "").toString().toLowerCase();

  const partitionDocs = () => {
  const documents = [];
  const forms = [];
  const references = [];

  docs.forEach((d) => {
    const id = normalizeId(d.id); // e.g. "newstarterpayroll", "staffregistration"

    // Explicit IDs that we know are forms
    const formIds = ["newstarterpayroll", "staffregistration"];

    const isReference = id.startsWith("reference") || id.startsWith("ref");

    const isForm =
      formIds.includes(id) ||          // your two forms
      id.includes("form") ||           // any future *Form ids
      id.includes("bank") ||
      id.includes("details") ||
      id.includes("payroll") ||        // just in case
      id.includes("registration");     // just in case

    if (isReference) {
      references.push(d);
    } else if (isForm) {
      forms.push(d);
    } else {
      documents.push(d);
    }
  });

  return { documents, forms, references };
};


  const { documents, forms, references } = partitionDocs();

  const getActiveList = () => {
    if (activeTab === "forms") return forms;
    if (activeTab === "references") return references;
    return documents; // default
  };

  const activeList = getActiveList();

  const tabStyle = (tab) => ({
    flex: 1,
    padding: "10px 0",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 13,
    textAlign: "center",
    borderBottom:
      activeTab === tab ? "3px solid #0284c7" : "3px solid transparent",
    color: activeTab === tab ? "#0284c7" : "#6b7280",
    transition: "border-bottom 0.2s ease, color 0.2s ease",
  });

  const tableHeaderCell = {
    padding: 8,
    textAlign: "left",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  };

  // ---------- RENDER ----------

  return (
    <div
      style={{
        paddingTop: 20,
        minHeight: "100vh",
        backgroundColor: "rgba(55,65,81,0.1)",
        color: "#1f2937",
        padding: "1.5rem",
      }}
    >
      <div
        style={{
          maxWidth: 750,
          margin: "0 auto",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          padding: 20,
        }}
      >
        <h2
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          <div
			  style={{
				width: 62,
				height: 62,
				borderRadius: "50%",
				border: "2px solid #9ca3af", // 👈 wider border
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#fff",
				boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
				marginRight: 12,
			  }}
			>
			  <img
				src={logoImg}
				alt="Convalesce Logo"
				style={{
				  width: 40,
				  height: 40,
				  borderRadius: "50%",
				}}
			  />
			</div>

          Compliance Overview
        </h2>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
          Please ensure your documents and mandatory training are valid and up
          to date.
        </p>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontWeight: 600, marginBottom: 4 }}>Overall Progress</h3>
          <ProgressBar value={summary.overall} />
          <p style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>
            {summary.overall}% Complete
          </p>
          {synced ? (
            <div style={{ color: "#16a34a", fontSize: 13, marginTop: 6 }}>
              ✅ Synced with admin
            </div>
          ) : (
            <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 6 }}>
              ⚠️ Waiting for admin sync...
            </div>
          )}
        </div>

        <hr style={{ margin: "16px 0" }} />

        <div>
          <h4 style={{ fontWeight: 600, marginBottom: 6 }}>
            Mandatory Documents Progress
          </h4>
          <ProgressBar value={summary.mandatory} color="#16a34a" />
          <p style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>
            {summary.mandatory}% Complete
          </p>
        </div>

        {/* Collapsible Details */}
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}
            style={{
              marginTop: 16,
              textAlign: "left",
              background: "#f9fafb",
              padding: 16,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              Mandatory compliance: {summary.mandatoryPct}%
            </div>
            <ProgressBar value={summary.mandatoryPct} />

            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginTop: 10,
                marginBottom: 8,
              }}
            >
              Document & Training compliance: {summary.overallPct}%
            </div>
            <ProgressBar value={summary.overallPct} />

            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginTop: 10,
                marginBottom: 8,
              }}
            >
              Forms compliance: {progressPercent}%
            </div>
            <ProgressBar value={progressPercent} />

            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginTop: 10,
                marginBottom: 8,
              }}
            >
              References & CV compliance: {referenceProgress}%
            </div>
            <ProgressBar value={referenceProgress} />
          </motion.div>
        )}

        {/* ---------- THREE-TABBED MENU ---------- */}
        <div style={{ marginTop: 24 }}>
          <h4 style={{ fontWeight: 600, marginBottom: 10 }}>
            Your Compliance Items
          </h4>

          {/* Tab headers */}
          <div
            style={{
              display: "flex",
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              marginBottom: 12,
            }}
          >
            <div
              style={tabStyle("documents")}
              onClick={() => setActiveTab("documents")}
            >
              Documents
            </div>
            <div
              style={tabStyle("forms")}
              onClick={() => setActiveTab("forms")}
            >
              Forms
            </div>
            <div
              style={tabStyle("references")}
              onClick={() => setActiveTab("references")}
            >
              References
            </div>
          </div>

          {/* Tab content */}
          {loading ? (
            <p style={{ fontSize: 13 }}>Loading documents...</p>
          ) : activeList.length === 0 ? (
            <p style={{ fontSize: 13, color: "#6b7280" }}>
              No items in this section yet.
            </p>
          ) : (
            <table style={tableStyle}>
              <thead style={{ backgroundColor: "#f3f4f6" }}>
                <tr>
                  <th style={tableHeaderCell}>Item</th>
                  <th style={{ ...tableHeaderCell, textAlign: "center" }}>
                    Renewal
                  </th>
                  <th style={{ ...tableHeaderCell, textAlign: "center" }}>
                    Status
                  </th>
                  <th style={{ ...tableHeaderCell, textAlign: "center" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeList.map((d) => (
                  <tr
                    key={d.id}
                    style={{ borderBottom: "1px solid #e5e7eb" }}
                  >
                    <td style={{ padding: 8 }}>{d.name || d.id}</td>
                    <td style={{ textAlign: "center", padding: 8 }}>
                      {d.renewal || "—"}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        padding: 8,
                        color: d.verified
                          ? "#16a34a"
                          : d.status === "uploaded"
                          ? "#2563eb"
                          : "#b91c1c",
                      }}
                    >
                      {d.verified
                        ? "Verified"
                        : d.status === "uploaded"
                        ? "Uploaded"
                        : "Missing"}
                    </td>
					
					
					
                   <td style={{ textAlign: "center", padding: 8 }}>

  {/* NEW STARTER PAYROLL */}
  {normalizeId(d.id) === "newstarterpayroll" && (
    <a
      href="/forms/new-starter-payroll"
      style={{
        color: "#0284c7",
        textDecoration: "underline",
        fontWeight: 600,
        fontSize: 13,
      }}
    >
      {d.status === "submitted" ? "View Submission" : "Fill Form"}
    </a>
  )}

 
  
  
{/* STAFF REGISTRATION FORM */}
{normalizeId(d.id) === "staffregistration" && (
  <a
    href="/forms/staff-registration-short"
    style={{
      color: "#0284c7",
      textDecoration: "underline",
      fontWeight: 600,
      fontSize: 13,
    }}
  >
    {d.status === "submitted" ? "View Submission" : "Fill Form"}
  </a>
)}



</td>


                  </tr>
				  
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            marginTop: 20,
            padding: "12px",
            border: "1px dashed #d1d5db",
            borderRadius: 8,
            background: "#f9fafb",
          }}
        >
          <p style={{ fontSize: 13, color: "#4b5563" }}>
            📩 To update or send new documents, please email or WhatsApp the
            admin:
          </p>
          <div style={{ marginTop: 6 }}>
            <a
              href="mailto:compliace@convalesceuk.com"
              style={{
                color: "#2563eb",
                textDecoration: "underline",
                marginRight: 10,
              }}
            >
              Email Admin
            </a>
            <a
              href="https://wa.me/+447599121308"
              target="_blank"
              rel="noreferrer"
              style={{
                color: "#22c55e",
                textDecoration: "underline",
              }}
            >
              WhatsApp Admin
            </a>
			
			<a
    href="/forms/staff-registration-full"
	
	style={{
               
                textDecoration: "underline",
              }}
	> Full form
	</a>
          </div>
        </div>
      </div>
    </div>
  );
}
